import { TooltipContent, ObservationContent, TitleContent } from './ElementTooltipContent';
import { ObservationData } from '../../Types';

export class Tooltip {
  private readonly tooltipElement: HTMLDivElement;
  private readonly contentContainer: HTMLDivElement;
  private isVisible: boolean = false;

  constructor() {
    this.tooltipElement = document.createElement('div');
    this.tooltipElement.id = 'Tooltip';
    this.tooltipElement.style.position = 'absolute';
    this.tooltipElement.style.padding = '15px';
    this.tooltipElement.style.backgroundColor = 'rgba(0, 0, 0, 0.6)';
    this.tooltipElement.style.color = '#fff';
    this.tooltipElement.style.borderRadius = '10px';
    this.tooltipElement.style.pointerEvents = 'none';
    this.tooltipElement.style.fontSize = '14px';
    this.tooltipElement.style.opacity = '0';
    this.tooltipElement.style.transform = 'translate(-50%, -10px)';
    this.tooltipElement.style.transition = 'opacity 0.3s ease, transform 0.3s ease';
    this.tooltipElement.style.display = 'none';
    this.tooltipElement.style.maxWidth = '300px';
    this.tooltipElement.style.textAlign = 'center';
    this.tooltipElement.style.border = '2px solid #fff';
    this.tooltipElement.style.boxShadow = '0 4px 8px rgba(0, 0, 0, 0.2)';

    this.contentContainer = document.createElement('div');
    this.tooltipElement.appendChild(this.contentContainer);

    document.body.appendChild(this.tooltipElement);
  }

  /**
   * Shows the tooltip with the provided content
   * @param content - The content to display in the tooltip
   * @param x - X coordinate for positioning
   * @param y - Y coordinate for positioning
   */
  public show(content: TooltipContent, x: number, y: number): void {
    this.contentContainer.innerHTML = '';
    content.render(this.contentContainer);
    this.updatePosition(x, y);
    this.tooltipElement.style.display = 'block';
    this.tooltipElement.style.opacity = '1';
    this.tooltipElement.style.transform = 'translate(-50%, 0)';
    this.isVisible = true;
  }

  /**
   * Creates a tooltip content for observation data
   * @param data - Array of observation data
   * @returns A TooltipContent instance for observation data
   */
  public static createObservationContent(data: ObservationData[]): TooltipContent {
    return new ObservationContent(data);
  }

  /**
   * Creates a tooltip content for a title
   * @param title - The title to display
   * @returns A TooltipContent instance for a title
   */
  public static createTitleContent(title: string): TooltipContent {
    return new TitleContent(title);
  }

  /**
   * Hides the tooltip, setting it to invisible and moving it off-screen after a transition.
   */
  public hide(): void {
    if (!this.isVisible) return;
    this.tooltipElement.style.opacity = '0';
    this.tooltipElement.style.transform = 'translate(-50%, -10px)';
    setTimeout(() => {
      this.tooltipElement.style.display = 'none';
      this.isVisible = false;
    }, 300);
  }

  /**
   * Updates the position of the tooltip based on the specified x and y coordinates.
   * Ensures the tooltip remains within the viewport.
   * @param x - The x-coordinate for positioning the tooltip.
   * @param y - The y-coordinate for positioning the tooltip.
   */
  private updatePosition(x: number, y: number): void {
    let tooltipX = x + 10;
    let tooltipY = y + 10;

    const tooltipRect = this.tooltipElement.getBoundingClientRect();
    if (tooltipX + tooltipRect.width > window.innerWidth) {
      tooltipX = x - tooltipRect.width - 10;
    }
    if (tooltipY + tooltipRect.height > window.innerHeight) {
      tooltipY = y - tooltipRect.height - 10;
    }

    this.tooltipElement.style.left = `${tooltipX}px`;
    this.tooltipElement.style.top = `${tooltipY}px`;
  }

  /**
   * Removes the tooltip element from the DOM.
   */
  public destroy(): void {
    document.body.removeChild(this.tooltipElement);
  }
}
